import http from '../http-common';

/**
 * Client for the per-video truth label.
 * A video has at most ONE label: either a lie with a single temporal range
 * [incoherence_start_ms, incoherence_end_ms), or a truthful mark with no range.
 */
class IncoherenceService {
  /**
   * Get the label for a given analysis source.
   * Resolves { lie: { label_type: 'incoherence'|'truthful', incoherence_start_ms|null, incoherence_end_ms|null, ... } | null }.
   * @param {'interview'|'manual_analysis'} sourceType
   * @param {number} sourceId
   */
  get(sourceType, sourceId) {
    return http.get(`/video-incoherence/${sourceType}/${sourceId}`);
  }

  /**
   * Create or update (upsert) the label for a source.
   * Omitting label_type means 'incoherence' (backward compatible) and requires the range;
   * label_type 'truthful' must NOT carry a range (stored with a NULL range).
   * @param {'interview'|'manual_analysis'} sourceType
   * @param {number} sourceId
   * @param {{ label_type?: 'incoherence'|'truthful', incoherence_start_ms?: number, incoherence_end_ms?: number }} payload
   */
  upsert(sourceType, sourceId, payload) {
    return http.put(`/video-incoherence/${sourceType}/${sourceId}`, payload);
  }

  /**
   * Remove the label from a source.
   * @param {'interview'|'manual_analysis'} sourceType
   * @param {number} sourceId
   */
  remove(sourceType, sourceId) {
    return http.delete(`/video-incoherence/${sourceType}/${sourceId}`);
  }

  /**
   * Get the label-based evaluation metrics, reported per coherence score method.
   * Resolves { global, byVideo } where:
   * - global: { videos_total, videos_labeled, videos_with_incoherence, videos_truthful,
   *   videos_evaluated, default_threshold_pct, methods: { computed_subtraction,
   *   alert_major }, ...legacy flat fields mirroring computed_subtraction }.
   *   Each method carries { incoherents_detected, incoherents_detected_pct, truthful_flagged,
   *   truthful_flagged_pct, correct_videos, correctness_pct,
   *   correctness_pct_over_total, videos_with_false_positive,
   *   false_positive_video_pct, false_positive_pct,
   *   false_positive_pct_over_all_ideas, subtracting_ideas_total,
   *   false_positive_ideas_total }.
   * - byVideo[]: { source_type, source_id, video_name, label_type,
   *   incoherence_start_ms|null, incoherence_end_ms|null, threshold_pct, analysis_available,
   *   ideas_total, total_ideas|null, methods: { computed_subtraction, alert_major },
   *   ...legacy flat fields }. Each method carries { subtracting_ideas,
   *   overlapping_ideas|null, false_positive_ideas, has_false_positive, is_correct|null }.
   * With a `groupId` (M4 contract 6) the evaluation set is the group's members with their
   * resolved runs and the payload adds `scope` `{ kind, group_id, threshold_pct, series_key,
   * mixed_series, engine_versions, config }` plus the anti-survivorship buckets; without it the
   * global (active-run) behaviour is unchanged.
   * @param {{ threshold?: number, groupId?: number|string|null, group_id?: number|string|null }} params
   *   optional threshold override (percentage 0-100) and group scope
   */
  getMetrics(params = {}) {
    const { groupId, group_id: groupIdSnake, ...rest } = params || {};
    const query = { ...rest };
    const scopeId = groupId ?? groupIdSnake;
    if (scopeId !== undefined && scopeId !== null && scopeId !== '') query.group_id = scopeId;
    return http.get('/video-incoherence/metrics', { params: query });
  }
}

export default new IncoherenceService();
